import React, { useState } from "react";
import PropTypes from "prop-types";
import { ConfirmModal } from "../../../../boxes/ConfirmModal/ConfirmModal";
import { uiNotification } from "../../../../services/UINotificationService";
import { HOLD_TYPES } from "../../../../store/event/consts";

/*
 * Shown after a text2pay booking. The slot is held but the appointment is not
 * confirmed until the link is completed, so this states what happens next and always
 * offers the link — the text can fail while the booking itself succeeds.
 *
 * A hold either owes a deposit or only needs a card on file for the cancellation
 * policy. Nothing is owed in the second case, so it gets its own wording rather than
 * telling the staff the patient has to pay.
 */
const COPY = {
  [HOLD_TYPES.deposit]: {
    title: "Appointment is on Hold - Awaiting Service Deposit Payment",
    sent: "We’ve texted the patient a secure payment link to pay the required service deposit. Once the payment is completed, the card will be securely saved to the patient’s profile and can be used for the Cancellation Policy, if applicable.",
    notSent:
      "We could not text the patient the secure payment link to pay the required service deposit. Share it with them — once the payment is completed, the card will be securely saved to the patient’s profile and can be used for the Cancellation Policy, if applicable.",
    expiry: (minutes) =>
      `The appointment will be held for ${minutes} minutes. If the patient does not complete the payment within ${minutes} minutes, the appointment will be released.`,
    copyAction: "Copy payment link",
    noLink:
      "No payment link could be generated. Please rebook the appointment.",
  },
  [HOLD_TYPES.cardCapture]: {
    title: "Appointment is on Hold - Awaiting for card details",
    sent: "We’ve texted the patient a secure link to provide their card details and enroll in the Cancellation Policy.",
    notSent:
      "We could not text the patient the secure link to provide their card details and enroll in the Cancellation Policy. Share it with them to complete the card setup.",
    expiry: (minutes) =>
      `The appointment will be held for ${minutes} minutes. If the patient does not complete the card setup within ${minutes} minutes, the appointment will be released.`,
    copyAction: "Copy card details link",
    noLink: "No link could be generated. Please rebook the appointment.",
  },
};

const AppointmentPendingPaymentModal = ({
  isOpen,
  onClose,
  paymentLink,
  isLinkSent,
  expiresInMinutes,
  holdType,
}) => {
  const [isCopied, setIsCopied] = useState(false);
  const copy = COPY[holdType] || COPY[HOLD_TYPES.deposit];

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(paymentLink);
      setIsCopied(true);
    } catch (e) {
      uiNotification.error("Could not copy the link");
    }
  };

  return (
    <ConfirmModal
      isOpen={isOpen}
      onClose={onClose}
      /* Done is the primary action; ConfirmModal renders onConfirm first, so it sits left */
      onConfirm={onClose}
      onCancel={paymentLink ? onCopy : undefined}
      confirmTitle="Done"
      cancelTitle={isCopied ? "Copied" : copy.copyAction}
      title={copy.title}
    >
      <div>{isLinkSent ? copy.sent : copy.notSent}</div>
      {expiresInMinutes && (
        <div className="m-t-10">{copy.expiry(expiresInMinutes)}</div>
      )}
      {!paymentLink && <div className="m-t-10">{copy.noLink}</div>}
    </ConfirmModal>
  );
};

AppointmentPendingPaymentModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  paymentLink: PropTypes.string,
  isLinkSent: PropTypes.bool,
  expiresInMinutes: PropTypes.number,
  holdType: PropTypes.oneOf(Object.values(HOLD_TYPES)),
};

AppointmentPendingPaymentModal.defaultProps = {
  paymentLink: "",
  isLinkSent: false,
  expiresInMinutes: null,
  holdType: HOLD_TYPES.deposit,
};

export default AppointmentPendingPaymentModal;
